import { type FC, useEffect, useMemo, useRef, useState } from "react";

import Button from "@/design-system/button";
import { languageSelector } from "@/src/contexts";
import { browserStatics } from "@/src/shared/BrowserStatic";
import { formatAmount } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import type { ICreditCardInfo } from "../common/CreditCardInfo";
import { CreditCardInfo } from "../common/CreditCardInfo";
import resourcesPl from "./CashbackChart.Resources";
import resourcesEn from "./CashbackChart.Resources.En";
import type { ICashbackTooltip } from "./CashbackTooltip";
import CashbackTooltip from "./CashbackTooltip";

import * as css from "./CashbackChart.module.css";

export interface ICashbackChart {
    credits: number;
    pending: number;
    noFundsMessage: string;
    automaticWithdrawalsInfo?: ICreditCardInfo;
    cashbackTooltipByEmail?: ICashbackTooltip;
    onWithdrawalClick?: () => void;
}

const unitValue = 100;
const withdrawCircleStroke = 3.4;
const pendingCircleStroke = 3.4;
const standardCircleStroke = 3.4;
const viewSize = unitValue + withdrawCircleStroke;
const cValue = viewSize / 2;
const maxCircleStrokeWidth = 2;
const maxRadius = unitValue / 2 - maxCircleStrokeWidth;
const maxRadiusPercentage = maxRadius / 100;
const rValue = unitValue * maxRadiusPercentage;
const cPerimeter = 2 * Math.PI * unitValue * maxRadiusPercentage;
const offset = -cPerimeter * 0.375;
const outerStrokeDashArray = `${cPerimeter * 0.75} ${cPerimeter * 0.25}`;

const animationEvolution = 1 / 60;

const CashbackChart: FC<ICashbackChart> = ({
    credits = 0,
    noFundsMessage,
    pending = 0,
    automaticWithdrawalsInfo,
    cashbackTooltipByEmail,
    onWithdrawalClick,
}) => {
    const [animationStatus, setAnimationStatus] = useState(0);
    const animationRef = useRef<number>(0);

    useEffect(() => {
        animationRef.current = requestAnimationFrame(() =>
            setAnimationStatus((currState) => {
                const currentStatus = currState + animationEvolution;
                return currentStatus > 1 ? 1 : currentStatus;
            }),
        );

        return () => {
            cancelAnimationFrame(animationRef.current);
        };
    }, [animationStatus]);

    const { scale, resources } = useMemo(() => {
        const maxValue = pending + credits;
        const scaleList = [20, 50, 100, 500, 2000, 10000, 100000];

        return {
            scale: scaleList.find((n) => n > maxValue) || maxValue * 1.2,
            resources: languageSelector(resourcesPl, resourcesEn),
        };
    }, [credits, pending]);

    const inputValue = (value: number, scale: number, totalScale: number): number =>
        (value / scale) * totalScale;

    const getMoneyDiv = (cash: number) => (
        <div className={css.value}>{formatAmount(cash * animationStatus, browserStatics.language)}</div>
    );

    const totalScale = cPerimeter * 0.75 * animationStatus;
    const creditsStroke = credits !== 0 ? inputValue(credits, scale, totalScale) : 6;
    let pendingStroke = pending !== 0 ? inputValue(pending, scale, totalScale) : 3;
    pendingStroke += creditsStroke;

    return (
        <div className={css.cashbackChartComponent}>
            <div className={css.graphWrapper}>
                <svg viewBox={`0 0 ${viewSize} ${viewSize}`}>
                    <circle
                        cx={cValue}
                        cy={cValue}
                        r={rValue}
                        fill="transparent"
                        strokeWidth={standardCircleStroke}
                        className={css.circle}
                        strokeDasharray={outerStrokeDashArray}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                    />
                    <circle
                        cx={cValue}
                        cy={cValue}
                        r={rValue}
                        fill="transparent"
                        strokeWidth={pendingCircleStroke}
                        className={css.withdrawCircle}
                        strokeDasharray={`${pendingStroke} ${cPerimeter - pendingStroke}`}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                    />
                    <circle
                        cx={cValue}
                        cy={cValue}
                        r={rValue}
                        fill="transparent"
                        strokeWidth={withdrawCircleStroke}
                        className={css.cashbackCircle}
                        strokeDasharray={`${creditsStroke} ${cPerimeter - creditsStroke}`}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                    />
                </svg>
                <div className={css.chartSection}>
                    <div className={css.textContainer}>
                        <div className={`${css.text} ${css.cashback}`}>
                            {getMoneyDiv(pending)}
                            <p dangerouslySetInnerHTML={{ __html: resources.pending }} />
                        </div>
                        <div className={`${css.text} ${css.withdraw}`}>
                            {getMoneyDiv(credits)}
                            <p dangerouslySetInnerHTML={{ __html: resources.credits }} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={css.textSection}>
                {cashbackTooltipByEmail && <CashbackTooltip {...cashbackTooltipByEmail} />}
                {!noFundsMessage && (
                    <Button
                        as="button"
                        size="M"
                        variant="secondary"
                        onClick={onWithdrawalClick}
                        styleX={styles.button}
                    >
                        {resources.withdrawButton}
                    </Button>
                )}
                {automaticWithdrawalsInfo === undefined && noFundsMessage && (
                    <p className={css.noFundsMessage} dangerouslySetInnerHTML={{ __html: noFundsMessage }} />
                )}
                {automaticWithdrawalsInfo && <CreditCardInfo {...automaticWithdrawalsInfo} />}
            </div>
        </div>
    );
};

const styles = stylex.create({
    button: {
        marginBottom: "15px",
        width: "100%",
    },
});

export default CashbackChart;
